import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { apiService } from '../services/apiService';
import '../styles/Auth.css';
import PasswordInput from '../components/PasswordInput';
import { useTestAuthFlow } from '../hooks/useTestAuthFlow';
import { redirectToTenant } from '../utils/tenantResolver';
import { markOnboardingPendingForNextLogin } from '../utils/onboardingStorage';
import AuthPageTemplate from '../components/auth/AuthPageTemplate';
import {
  getPasswordChecks,
  PASSWORD_MIN_LENGTH,
  PASSWORD_MAX_LENGTH,
  validatePassword,
} from '../utils/passwordPolicy';

const NAME_MIN_LENGTH = 1;
const NAME_MAX_LENGTH = 12;
const ORGANIZATION_NAME_MIN_LENGTH = 3;
const ORGANIZATION_NAME_MAX_LENGTH = 30;

interface RegisterForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  organizationName: string;
}

interface RegisterErrors extends Partial<RegisterForm> {
  otp?: string;
  captcha?: string;
  plan?: string;
  billing?: string;
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { testAuthFlowEnabled, testStaticCaptcha } = useTestAuthFlow();
  const isGetStarted = location.pathname === '/get-started' || location.pathname === '/signup';
  const { login, logout, isAuthenticated, isLoading: authLoading } = useAuth();
  const { isMainDomain, setOrganization } = useTenant();
  const [formData, setFormData] = useState<RegisterForm>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    organizationName: ''
  });
  const [errors, setErrors] = useState<RegisterErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [isProcessingAuth, setIsProcessingAuth] = useState(false);
  const [captchaText, setCaptchaText] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpForExistingLogin, setOtpForExistingLogin] = useState(false);
  const captchaCanvasRef = useRef<HTMLCanvasElement>(null);

  const [planChoice, setPlanChoice] = useState<string>('FREE');
  const [billingChoice, setBillingChoice] = useState<'MONTHLY' | 'YEARLY' | ''>('');
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);

  useEffect(() => {
    const p = searchParams.get('plan')?.toUpperCase();
    if (p === 'STANDARD' || p === 'PROFESSIONAL' || p === 'ENTERPRISE') {
      setPlanChoice(p);
    }
    const b = (searchParams.get('billingCycle') || '').toUpperCase();
    if (b === 'MONTHLY' || b === 'YEARLY') {
      setBillingChoice(b);
    }
  }, [searchParams]);

  const syncPlanBillingToUrl = (plan: string, billing: 'MONTHLY' | 'YEARLY' | '') => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (plan === 'STANDARD' || plan === 'PROFESSIONAL') {
          params.set('plan', plan);
          if (billing === 'MONTHLY' || billing === 'YEARLY') {
            params.set('billingCycle', billing.toLowerCase());
          } else {
            params.delete('billingCycle');
          }
        } else if (plan === 'ENTERPRISE') {
          params.set('plan', 'ENTERPRISE');
          params.delete('billingCycle');
        } else {
          params.delete('plan');
          params.delete('billingCycle');
        }
        return params;
      },
      { replace: true }
    );
  };

  const validatePlanBilling = (): Pick<RegisterErrors, 'plan' | 'billing'> => {
    const out: Pick<RegisterErrors, 'plan' | 'billing'> = {};
    if (planChoice === 'STANDARD' || planChoice === 'PROFESSIONAL') {
      if (!billingChoice) {
        out.billing = 'Select monthly or yearly billing.';
      }
    }
    return out;
  };

  // Extract exchange token from URL if present (cross-subdomain authentication)
  useEffect(() => {
    const exchangeToken = searchParams.get('exchangeToken');
    
    if (exchangeToken) {
      setIsProcessingAuth(true);
      
      // Exchange the temporary token for the real JWT
      apiService.exchangeToken(exchangeToken)
        .then(data => {
          if (data.success && data.token && data.user) {
            // Save auth data to this subdomain's localStorage
            login(data.user, data.token);
            
            // Set organization context
            if (data.user.organizationSlug) {
              setOrganization({
                id: data.user.organizationId || 0,
                name: '',
                slug: data.user.organizationSlug,
                planType: data.user.planType || 'FREE'
              });
            }
            
            // Clean URL and navigate to dashboard
            setTimeout(() => {
              navigate('/dashboard', { replace: true });
            }, 100);
          } else {
            console.error('Exchange token failed:', data.message);
            setIsProcessingAuth(false);
          }
        })
        .catch(error => {
          console.error('Error exchanging token:', error);
          setIsProcessingAuth(false);
        });
    }
  }, [searchParams, login, setOrganization, navigate]);

  // If user is already authenticated and on tenant domain, redirect to dashboard
  useEffect(() => {
    if (!authLoading && isAuthenticated && !isMainDomain && !isProcessingAuth) {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, isAuthenticated, isMainDomain, isProcessingAuth, navigate]);

  const getPasswordStrength = (password: string): { strength: string; message: string } => {
    if (!password) return { strength: '', message: '' };
    
    const checks = getPasswordChecks(password);
    let score = Object.values(checks).filter(Boolean).length;

    if (score === 0) return { strength: '', message: '' };
    if (score <= 2) return { strength: 'weak', message: 'Weak' };
    if (score <= 3) return { strength: 'fair', message: 'Fair' };
    if (score <= 4) return { strength: 'good', message: 'Good' };
    return { strength: 'strong', message: 'Strong' };
  };

  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const [passwordStrength, setPasswordStrength] = useState({ strength: '', message: '' });

  // Update password checks and strength when password changes
  useEffect(() => {
    setPasswordChecks(getPasswordChecks(formData.password));
    setPasswordStrength(getPasswordStrength(formData.password));
  }, [formData.password]);

  const drawCaptcha = (value: string) => {
    const canvas = captchaCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = 140;
    canvas.height = 44;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#f4f6fb';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.font = 'bold 22px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#1f2937';
    ctx.fillText(value, canvas.width / 2, canvas.height / 2);

    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, 0.28)`;
      ctx.fillRect(Math.random() * canvas.width, Math.random() * canvas.height, 2, 2);
    }
  };

  const generateCaptcha = (clearCaptchaError = true) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let code = '';
    for (let i = 0; i < 5; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaText(code);
    setCaptchaInput('');
    if (clearCaptchaError) {
      setErrors((prev) => ({ ...prev, captcha: undefined }));
    }
    drawCaptcha(code);
  };

  const applyOtpSendError = (text: string) => {
    const normalized = text.toLowerCase();
    if (normalized.includes('already exists') || normalized.includes('already registered')) {
      setErrors((prev) => ({
        ...prev,
        email: 'This email is already registered. Please sign in using your existing account.',
      }));
      setMessage({
        text: 'This email is already registered. Please sign in using your existing account.',
        type: 'error',
      });
      return;
    }
    setMessage({ text, type: 'error' });
  };

  const sendOtpToEmail = async () => {
    if (!formData.email.trim()) {
      setErrors((prev) => ({ ...prev, email: 'Email is required before sending OTP' }));
      return;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email.trim())) {
      setErrors((prev) => ({ ...prev, email: 'Email is invalid' }));
      return;
    }

    setIsLoading(true);
    setMessage(null);
    try {
      const response = await apiService.sendAuthOtp({
        email: formData.email.trim().toLowerCase(),
        purpose: 'REGISTER',
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        password: formData.password,
      });
      if (response.success) {
        setOtpSent(true);
        setOtpForExistingLogin(Boolean((response as { existingAccountLogin?: boolean }).existingAccountLogin));
        setMessage({ text: response.message || 'OTP sent to your email', type: 'success' });
      } else {
        const text = response.message || 'Failed to send OTP';
        applyOtpSendError(text);
      }
    } catch (error: any) {
      const data = error.response?.data;
      const apiMessage =
        (typeof data === 'object' && data !== null && typeof data.message === 'string'
          ? data.message
          : null) ||
        (typeof data === 'object' && data !== null && typeof data.error === 'string'
          ? data.error
          : null) ||
        (typeof data === 'string' ? data : null);
      const status = error.response?.status;
      const fallback =
        status === 502
          ? 'Unable to send OTP email. Please try again in a moment.'
          : status === 400
            ? 'Could not send OTP. Check your email or use a different address.'
            : error.message || 'Failed to send OTP. Check your email address and try again.';
      applyOtpSendError(apiMessage || fallback);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  useEffect(() => {
    if (captchaText) {
      drawCaptcha(captchaText);
    }
  }, [captchaText]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // For first/last name only allow alphabetic characters (A–Z, a–z)
    let processedValue = value.replace(/\s+/g, '');
    if (name === 'firstName' || name === 'lastName') {
      processedValue = processedValue.replace(/[^a-zA-Z]/g, '').slice(0, NAME_MAX_LENGTH);
    } else if (name === 'organizationName') {
      processedValue = processedValue.slice(0, ORGANIZATION_NAME_MAX_LENGTH);
    }

    if (name !== 'otpInput') {
      setOtpSent(false);
      setOtpInput('');
      setOtpForExistingLogin(false);
    }

    // Update form data
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: processedValue
      };
      return newData;
    });

    // Clear error for this field when user starts typing
    if (errors[name as keyof RegisterForm]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validateBaseForm = (): RegisterErrors => {
      const baseErrors: RegisterErrors = {};

      const firstNameTrimmed = formData.firstName.trim();
      if (!firstNameTrimmed) {
        baseErrors.firstName = 'First name is required';
      } else if (firstNameTrimmed.length < NAME_MIN_LENGTH || firstNameTrimmed.length > NAME_MAX_LENGTH) {
        baseErrors.firstName = `First name must be ${NAME_MIN_LENGTH}–${NAME_MAX_LENGTH} characters`;
      }

      const lastNameTrimmed = formData.lastName.trim();
      if (!lastNameTrimmed) {
        baseErrors.lastName = 'Last name is required';
      } else if (lastNameTrimmed.length < NAME_MIN_LENGTH || lastNameTrimmed.length > NAME_MAX_LENGTH) {
        baseErrors.lastName = `Last name must be ${NAME_MIN_LENGTH}–${NAME_MAX_LENGTH} characters`;
      }

      const emailTrimmed = formData.email.trim();
      if (!emailTrimmed) {
        baseErrors.email = 'Email is required';
      } else if (!/^[A-Za-z0-9+_.-]+@(.+)$/.test(emailTrimmed)) {
        baseErrors.email = 'Email is invalid';
      }

      const organizationNameTrimmed = (formData.organizationName || '').trim();
      if (!organizationNameTrimmed) {
        baseErrors.organizationName = 'Organization name is required';
      } else if (
        organizationNameTrimmed.length < ORGANIZATION_NAME_MIN_LENGTH
        || organizationNameTrimmed.length > ORGANIZATION_NAME_MAX_LENGTH
      ) {
        baseErrors.organizationName = `Organization name must be ${ORGANIZATION_NAME_MIN_LENGTH}–${ORGANIZATION_NAME_MAX_LENGTH} characters`;
      }

      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        baseErrors.password = passwordValidation.error;
      }

      if (!formData.confirmPassword || !formData.confirmPassword.trim()) {
        baseErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        baseErrors.confirmPassword = 'Passwords do not match';
      }

      if (!captchaInput.trim()) {
        baseErrors.captcha = 'Security code is required';
      } else if (
        captchaInput.trim() !== captchaText
        && !(testAuthFlowEnabled && captchaInput.trim() === testStaticCaptcha)
      ) {
        baseErrors.captcha = 'Invalid captcha (case-sensitive)';
      }

      return baseErrors;
    };

    if (planChoice === 'ENTERPRISE') {
      const baseErrors = validateBaseForm();
      if (Object.keys(baseErrors).length > 0) {
        setErrors(baseErrors);
        if (baseErrors.captcha?.toLowerCase().includes('invalid')) generateCaptcha(false);
        return;
      }
      navigate('/contact-sales');
      return;
    }

    if (!otpSent) {
      const baseErrors = validateBaseForm();
      const planErrors = validatePlanBilling();
      const merged = { ...baseErrors, ...planErrors };
      setErrors(merged);
      if (Object.keys(merged).length > 0) {
        if (merged.captcha?.toLowerCase().includes('invalid')) generateCaptcha(false);
        return;
      }
      if (testAuthFlowEnabled && captchaInput.trim() === testStaticCaptcha) {
        setOtpSent(true);
        setMessage({
          text: 'OTP has been sent to your email address',
          type: 'success',
        });
        return;
      }
      await sendOtpToEmail();
      return;
    }

    const newErrors: RegisterErrors = { ...validateBaseForm(), ...validatePlanBilling() };

    if (!otpInput.trim()) {
      newErrors.otp = 'OTP is required';
    } else if (!/^\d{6}$/.test(otpInput.trim())) {
      newErrors.otp = 'OTP must be 6 digits';
    } else if (/^(\d)\1{5}$/.test(otpInput.trim())) {
      newErrors.otp = 'OTP cannot contain all same digits';
    }
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      if (newErrors.captcha?.toLowerCase().includes('invalid')) {
        generateCaptcha(false);
      }
      return;
    }

    setIsLoading(true);
    setMessage(null);

    if (otpForExistingLogin) {
      try {
        const data = await apiService.login({
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          otp: otpInput.trim(),
        });
        if (data.success && data.user && data.token) {
          login(data.user, data.token);
          if (data.user.organizationSlug) {
            setOrganization({
              id: data.user.organizationId || 0,
              name: formData.organizationName || `${formData.firstName} ${formData.lastName}`,
              slug: data.user.organizationSlug,
              planType: data.user.planType || 'FREE',
            });
          }
          setMessage({ text: 'Sign-in successful! Redirecting…', type: 'success' });
          if (isMainDomain && data.user.organizationSlug) {
            redirectToTenant(data.user.organizationSlug, '/dashboard');
          } else {
            navigate('/dashboard');
          }
          return;
        }
        setMessage({ text: data.message || 'Sign-in failed', type: 'error' });
      } catch (error: any) {
        const msg = error.response?.data?.message || 'Network error. Please try again.';
        setMessage({ text: msg, type: 'error' });
      } finally {
        setIsLoading(false);
      }
      return;
    }

    try {
      const data = await apiService.register({
        ...formData,
        otp: otpInput.trim(),
        selectedPlan: planChoice,
        ...(planChoice === 'STANDARD' || planChoice === 'PROFESSIONAL'
          ? { billingCycle: billingChoice }
          : {}),
      });

      if (data.success) {
        if (planChoice === 'STANDARD' || planChoice === 'PROFESSIONAL') {
          if (data.pendingPaidCheckout && data.token) {
            try {
              localStorage.setItem('token', data.token);
              localStorage.removeItem('user');
            } catch {
              /* ignore */
            }
          } else if (data.user && data.token) {
            login(data.user, data.token);
            if (data.user.organizationSlug) {
              setOrganization({
                id: data.user.organizationId || 0,
                name: formData.organizationName || `${formData.firstName} ${formData.lastName}`,
                slug: data.user.organizationSlug,
                planType: data.user.planType || 'FREE',
              });
            }
          }
          setCheckoutSubmitting(true);
          setMessage({
            text: 'Redirecting to secure payment…',
            type: 'success',
          });
          try {
            sessionStorage.setItem(
              'pendingSignupVerifyEmail',
              formData.email.trim().toLowerCase()
            );
          } catch {
            /* ignore */
          }
          const sessionRes = await apiService.createSignupPaymentSession({
            email: formData.email.trim().toLowerCase(),
            plan: planChoice,
            billing: billingChoice as 'MONTHLY' | 'YEARLY',
            returnBaseUrl: window.location.origin,
          });
          const redirectUrl = sessionRes.url || sessionRes.checkoutUrl;
          if (!sessionRes.success || !redirectUrl) {
            setCheckoutSubmitting(false);
            if (data.pendingPaidCheckout) {
              try {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
              } catch {
                /* ignore */
              }
            }
            setMessage({
              text: sessionRes.message || 'Could not start checkout. Try again or contact support.',
              type: 'error',
            });
            setIsLoading(false);
            return;
          }
          window.location.href = redirectUrl;
          return;
        } else {
          // FREE plan: No payment needed, activate user and org immediately
          setMessage({ text: 'Registration successful! Activating your account...', type: 'success' });
          
          const freeUser = data.user;
          if (!freeUser) {
            setMessage({ text: 'Registration incomplete. Please try again.', type: 'error' });
            return;
          }

          login(freeUser, data.token);
          if (freeUser.organizationSlug) {
            setOrganization({
              id: freeUser.organizationId || 0,
              name: formData.organizationName || `${formData.firstName} ${formData.lastName}`,
              slug: freeUser.organizationSlug,
              planType: freeUser.planType || 'FREE',
            });
          }

          // Activate FREE plan (user and org created with PENDING status, now activate them)
          try {
            if (freeUser.organizationId) {
              await apiService.activateFreePlan(freeUser.organizationId);
            }
          } catch (activationError: any) {
            console.error('Error activating FREE plan:', activationError);
            // Don't block user flow if activation fails - they can still use the system
            // Organization will remain PENDING but can be activated on first login
          }
          
          setTimeout(async () => {
            // FREE signup: user should land on Sign In and authenticate explicitly (no exchange-token auto-login).
            markOnboardingPendingForNextLogin();
            await logout();
            if (isMainDomain && freeUser.organizationSlug) {
              redirectToTenant(freeUser.organizationSlug, '/login?onboarding=1');
            } else {
              navigate('/login?onboarding=1');
            }
          }, 2000);
        }
      } else {
        setMessage({ text: data.message || 'Registration failed', type: 'error' });
      }
    } catch (error: any) {
      const msg = error.response?.data?.message || error.response?.data?.error || 'Network error. Please try again.';
      setMessage({ text: msg, type: 'error' });
      setCheckoutSubmitting(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageTemplate
      variant="register"
      leftImageSrc="/register-security-illustration.png"
      leftImageAlt="Secure registration illustration"
      style={{
        width: '100%',
        maxWidth: '100%',
        minHeight: 'calc((100vh - 64px) / 0.85)',
        boxSizing: 'border-box',
        fontSize: '0.8rem',
        lineHeight: 1.35,
        zoom: 0.85,
      }}
    >
      <div className="auth-card register-auth-card">
        <div className="auth-logo-wrapper">
          <img
            src="/dragonflytest-logo.jpg"
            alt="DragonflyTest Logo"
            className="auth-logo"
          />
        </div>
        <h2>{isGetStarted ? 'Get started' : 'Create Account'}</h2>
        <p className="auth-subtitle">
          {isGetStarted ? 'Create your workspace and choose a plan.' : 'Join us to start testing APIs'}
        </p>

        <p style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '10px' }}>
          You&apos;ll be redirected to secure payment to complete signup.
        </p>

        <div className="form-group">
          <label htmlFor="planChoice">Plan</label>
          <select
            id="planChoice"
            value={planChoice}
            onChange={(e) => {
              const v = e.target.value;
              const nextBilling = v === 'FREE' || v === 'ENTERPRISE' ? '' : billingChoice;
              setPlanChoice(v);
              if (v === 'FREE' || v === 'ENTERPRISE') {
                setBillingChoice('');
              }
              syncPlanBillingToUrl(v, nextBilling);
              setOtpSent(false);
              setOtpInput('');
              setErrors((prev) => ({ ...prev, plan: undefined, billing: undefined }));
            }}
            className={errors.billing ? 'error' : ''}
            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
          >
            <option value="FREE">FREE</option>
            <option value="STANDARD">STANDARD</option>
            <option value="PROFESSIONAL">PROFESSIONAL</option>
            {!isGetStarted && <option value="ENTERPRISE">ENTERPRISE</option>}
          </select>
        </div>

        {(planChoice === 'STANDARD' || planChoice === 'PROFESSIONAL') && (
          <div className="form-group">
            <span style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Billing</span>
            <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
              <label style={{ cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="billingChoice"
                  checked={billingChoice === 'MONTHLY'}
                  onChange={() => {
                    setBillingChoice('MONTHLY');
                    syncPlanBillingToUrl(planChoice, 'MONTHLY');
                    setErrors((prev) => ({ ...prev, billing: undefined }));
                  }}
                />{' '}
                Monthly
              </label>
              <label style={{ cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="billingChoice"
                  checked={billingChoice === 'YEARLY'}
                  onChange={() => {
                    setBillingChoice('YEARLY');
                    syncPlanBillingToUrl(planChoice, 'YEARLY');
                    setErrors((prev) => ({ ...prev, billing: undefined }));
                  }}
                />{' '}
                Yearly
              </label>
            </div>
            {errors.billing && <span className="error-text">{errors.billing}</span>}
          </div>
        )}
        
        {message && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={errors.firstName ? 'error' : ''}
                placeholder="Enter your first name"
                maxLength={NAME_MAX_LENGTH}
                minLength={NAME_MIN_LENGTH}
              />
              {errors.firstName && <span className="error-text">{errors.firstName}</span>}
              <small className="password-requirements-hint">
                First name must be {NAME_MIN_LENGTH}–{NAME_MAX_LENGTH} characters.
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={errors.lastName ? 'error' : ''}
                placeholder="Enter your last name"
                maxLength={NAME_MAX_LENGTH}
                minLength={NAME_MIN_LENGTH}
              />
              {errors.lastName && <span className="error-text">{errors.lastName}</span>}
              <small className="password-requirements-hint">
                Last name must be {NAME_MIN_LENGTH}–{NAME_MAX_LENGTH} characters.
              </small>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={errors.email ? 'error' : ''}
              placeholder="Enter your email"
            />
            {errors.email && <span className="error-text">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="organizationName">Organization Name <span style={{ color: 'var(--error-color, #c62828)' }}>*</span></label>
            <input
              type="text"
              id="organizationName"
              name="organizationName"
              value={formData.organizationName || ''}
              onChange={handleInputChange}
              className={errors.organizationName ? 'error' : ''}
              placeholder="e.g. Acme Corp"
              maxLength={ORGANIZATION_NAME_MAX_LENGTH}
              minLength={ORGANIZATION_NAME_MIN_LENGTH}
            />
            <small style={{ color: '#666', fontSize: '0.875rem' }}>
              Used for your tenant URL (e.g. yourcompany.crudexpert.com). Mandatory.
            </small>
            <small className="password-requirements-hint">
              Organization name must be {ORGANIZATION_NAME_MIN_LENGTH}–{ORGANIZATION_NAME_MAX_LENGTH} characters.
            </small>
            {errors.organizationName && <span className="error-text">{errors.organizationName}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <PasswordInput
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              hasError={!!errors.password}
              className={errors.password ? 'error' : ''}
              placeholder="Enter your password"
              maxLength={PASSWORD_MAX_LENGTH}
            />
            {formData.password && (
              <div className="password-strength">
                <div className="password-strength-meter">
                  <div className={`strength-${passwordStrength.strength}`}></div>
                </div>
                <span>Password Strength: {passwordStrength.message}</span>
              </div>
            )}
            {errors.password && <span className="error-text">{errors.password}</span>}
            <small className="password-requirements">
              Password must:
              <ul>
                <li className={passwordChecks.length ? 'valid' : ''}>
                  Be {PASSWORD_MIN_LENGTH}–{PASSWORD_MAX_LENGTH} characters long
                </li>
                <li className={passwordChecks.uppercase ? 'valid' : ''}>
                  Contain at least one uppercase letter
                </li>
                <li className={passwordChecks.lowercase ? 'valid' : ''}>
                  Contain at least one lowercase letter
                </li>
                <li className={passwordChecks.number ? 'valid' : ''}>
                  Contain at least one number
                </li>
                <li className={passwordChecks.special ? 'valid' : ''}>
                  Contain at least one special character (!@#$%^&*(),.?":{}|&lt;&gt; etc.)
                </li>
              </ul>
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              hasError={!!errors.confirmPassword}
              className={errors.confirmPassword ? 'error' : ''}
              placeholder="Confirm your password"
              maxLength={PASSWORD_MAX_LENGTH}
            />
            {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="captchaInput">Security Code</label>
            <div className="security-code-row">
              <canvas
                ref={captchaCanvasRef}
                onClick={() => generateCaptcha()}
                aria-label="Captcha challenge"
                className="security-code-canvas"
              />
              <button
                type="button"
                onClick={() => generateCaptcha()}
                disabled={isLoading}
                aria-label="Refresh security code"
                className="security-code-refresh"
              >
                ↻
              </button>
              <input
                type="text"
                id="captchaInput"
                name="captchaInput"
                value={captchaInput}
                onChange={(e) => {
                  setCaptchaInput(e.target.value.replace(/\s+/g, ''));
                  if (errors.captcha) setErrors((prev) => ({ ...prev, captcha: undefined }));
                }}
                className={`security-code-input ${errors.captcha ? 'error' : ''}`}
                placeholder="Enter security code"
                maxLength={5}
              />
            </div>
            {errors.captcha && <span className="error-text">{errors.captcha}</span>}
          </div>

          {otpSent && (
            <div className="form-group">
              <label htmlFor="otpInput">OTP Verification</label>
              <input
                type="text"
                id="otpInput"
                name="otpInput"
                value={otpInput}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setOtpInput(value);
                  if (errors.otp) setErrors((prev) => ({ ...prev, otp: undefined }));
                }}
                className={errors.otp ? 'error' : ''}
                placeholder="Enter 6-digit OTP"
                maxLength={6}
              />
              {errors.otp && <span className="error-text">{errors.otp}</span>}
              <div style={{ marginTop: '8px', textAlign: 'right' }}>
                <button
                  type="button"
                  className="link-button"
                  onClick={sendOtpToEmail}
                  disabled={isLoading}
                  style={{ fontSize: '14px' }}
                >
                  Resend OTP
                </button>
              </div>
            </div>
          )}

          <button type="submit" className="auth-button" disabled={isLoading || checkoutSubmitting}>
            {checkoutSubmitting
              ? 'Redirecting to payment…'
              : isLoading
                ? otpSent
                  ? 'Verifying...'
                  : 'Sending OTP...'
                : otpSent
                  ? 'Verify OTP & Create Account'
                  : isGetStarted
                    ? 'Continue'
                    : 'Create Account'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <button 
              className="link-button" 
              onClick={() => navigate('/login')}
            >
              Sign In
            </button>
          </p>
        </div>
      </div>
    </AuthPageTemplate>
  );
};

export default Register; 